import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid, Button, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import StyledTextField from '../ui/StyledTextField';
import StoneAutocomplete from '../autocomplete/StoneAutocomplete';

const StoneForm = ({ newStone, setNewStone, listePierre, pierreAutocompleteRef, setStones, stones, mode, setMode, editingStoneIndex, setEditingStoneIndex }) => {
    // Références pour les champs en mode 'liste'
    const qtyRefListe = useRef(null);
    const poidsRefListe = useRef(null);

    // Références pour les champs en mode 'libre'
    const descRefLibre = useRef(null);
    const qtyRefLibre = useRef(null);
    const poidsRefLibre = useRef(null);
    const euroRefLibre = useRef(null);
    const coefRefLibre = useRef(null);

    const handleStoneChange = (name, value) => {
        setNewStone(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (mode === 'libre') {
            // Si on n'a pas encore de newStone défini pour le mode libre
            setNewStone(prev => ({
                pierre: null,
                desc: prev?.desc || '',
                qte: prev?.qte || '',
                poids: prev?.poids || '',
                euro: prev?.euro || '',
                coef: prev?.coef || ''
            }));

            // Mettre le focus sur le champ Description après le render
            setTimeout(() => {
                if (descRefLibre.current) {
                    descRefLibre.current.focus();
                }
            }, 0);
        }
    }, [mode, setNewStone]);

    const handleAddStone = () => {
        if ((newStone.desc || newStone.pierre) && newStone.qte && newStone.poids) {
            const stoneData = {
                ...newStone.pierre,
                descriptionPierre: newStone.desc,
                qte: parseFloat(newStone.qte),
                lePoids: parseFloat(newStone.poids),
                euro: parseFloat(newStone.euro) || 0,
                coeff: parseFloat(newStone.coef) || 1,
            };
            if (editingStoneIndex !== null && editingStoneIndex >= 0) {
                const updated = [...stones];
                updated[editingStoneIndex] = { ...updated[editingStoneIndex], ...stoneData };
                setStones(updated);
            } else {
                setStones(prev => [...prev, stoneData]);
            }
            setNewStone({ pierre: null, desc: '', qte: '', poids: '', euro: '', coef: '' });
            setEditingStoneIndex(null);

            // Remettre le focus sur le premier champ selon le mode
            if (mode === 'liste') {
                pierreAutocompleteRef.current.focus();
            } else {
                descRefLibre.current.focus();
            }
        }
    };

    const isOkEnabled = () => {
        if (mode === 'liste') {
            return newStone.pierre && newStone.qte && newStone.poids;
        } else {
            return newStone.desc && newStone.qte && newStone.poids && newStone.euro && newStone.coef;
        }
    };

    // Gestion de la touche Entrée pour passer au champ suivant
    const handleKeyDown = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            } else {
                handleAddStone();
            }
        }
    };

    return (
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} className="no-print">
                <RadioGroup row value={mode} onChange={e => setMode(e.target.value)}>
                    <FormControlLabel value="liste" control={<Radio color="primary" />} label="Pierre de la liste" />
                    <FormControlLabel value="libre" control={<Radio color="primary" />} label="Pierre libre" />
                </RadioGroup>
            </Grid>

            {mode === 'liste' && (
                <>
                    <Grid item xs={12} sm={5}>
                        <StoneAutocomplete
                            ref={pierreAutocompleteRef}
                            listePierre={listePierre}
                            value={newStone.pierre}
                            onChange={(val) => {
                                if (val) {
                                    setNewStone({
                                        pierre: val,
                                        desc: val.descriptionPierre,
                                        qte: val.qte || 1,
                                        poids: val.lePoids || '',
                                        euro: val.euro || 0,
                                        coef: val.coeff || 1,
                                    });
                                    setTimeout(() => qtyRefListe.current?.focus(), 0);
                                } else {
                                    setNewStone({ pierre: null, desc: '', qte: '', poids: '', euro: '', coef: '' });
                                    setEditingStoneIndex(null);
                                }
                            }}
                            onEnter={handleAddStone}
                        />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <StyledTextField
                            label="Qté"
                            type="number"
                            value={newStone.qte || ''}
                            onChange={e => handleStoneChange('qte', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, poidsRefListe)}
                            variant="outlined"
                            size="small"
                            inputRef={qtyRefListe}
                        />
                    </Grid>
                    <Grid item xs={4} sm={3}>
                        <StyledTextField
                            label="Poids"
                            type="number"
                            value={newStone.poids || ''}
                            onChange={e => handleStoneChange('poids', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, null)}
                            variant="outlined"
                            size="small"
                            inputRef={poidsRefListe}
                        />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <Button fullWidth variant="contained" color="primary" onClick={handleAddStone} disabled={!isOkEnabled()}>
                            OK
                        </Button>
                    </Grid>
                </>
            )}

            {mode === 'libre' && (
                <>
                    <Grid item xs={12} sm={4}>
                        <StyledTextField
                            label="Description"
                            value={newStone.desc || ''}
                            onChange={e => handleStoneChange('desc', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, qtyRefLibre)}
                            variant="outlined"
                            size="small"
                            inputRef={descRefLibre}
                        />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <StyledTextField
                            label="Qté"
                            type="number"
                            value={newStone.qte || ''}
                            onChange={e => handleStoneChange('qte', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, poidsRefLibre)}
                            variant="outlined"
                            size="small"
                            inputRef={qtyRefLibre}
                        />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <StyledTextField
                            label="Poids"
                            type="number"
                            value={newStone.poids || ''}
                            onChange={e => handleStoneChange('poids', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, euroRefLibre)}
                            variant="outlined"
                            size="small"
                            inputRef={poidsRefLibre}
                        />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                        <StyledTextField
                            label="Prix €"
                            type="number"
                            value={newStone.euro || ''}
                            onChange={e => handleStoneChange('euro', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, coefRefLibre)}
                            variant="outlined"
                            size="small"
                            inputRef={euroRefLibre}
                        />
                    </Grid>
                    <Grid item xs={6} sm={1}>
                        <StyledTextField
                            label="Coef"
                            type="number"
                            value={newStone.coef || ''}
                            onChange={e => handleStoneChange('coef', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, null)}
                            variant="outlined"
                            size="small"
                            inputRef={coefRefLibre}
                        />
                    </Grid>
                    <Grid item xs={6} sm={1}>
                        <Button fullWidth variant="contained" color="primary" onClick={handleAddStone} disabled={!isOkEnabled()}>
                            OK
                        </Button>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

StoneForm.propTypes = {
    newStone: PropTypes.object.isRequired,
    setNewStone: PropTypes.func.isRequired,
    listePierre: PropTypes.array.isRequired,
    pierreAutocompleteRef: PropTypes.object.isRequired,
    setStones: PropTypes.func.isRequired,
    stones: PropTypes.array.isRequired,
    mode: PropTypes.string.isRequired,
    setMode: PropTypes.func.isRequired,
    editingStoneIndex: PropTypes.number,
    setEditingStoneIndex: PropTypes.func.isRequired,
};

export default StoneForm;
